import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

// Cambio de precios manual (sin tracking) desde el Monitor AD-P17.
// Escribe Regular/Diesel en Auto y el resto se llena por reglas (AD-P12 + Dif. Auto/Full de la estación).
export const ManualPriceChangeModal = ({ open, onOpenChange, row, fuels, onDone }) => {
  const [params, setParams] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [values, setValues] = useState({});
  const [include, setInclude] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !row) return;
    setNotes('');
    const init = {};
    fuels.forEach(f => {
      const p = row.prices?.[f.id] || {};
      init[f.id] = { self: p.self ?? '', full: p.full ?? '' };
    });
    setValues(init);
    setInclude({});
    api.get('/parameters').then(r => setParams(r.data)).catch(() => setParams(null));
    api.get(`/stations/${row.id}/pricing-config`).then(r => setCfg(r.data)).catch(() => setCfg(null));
  }, [open, row]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!row) return null;

  const avail = cfg?.available_products?.length ? cfg.available_products : fuels.map(f => f.id);
  const shownFuels = fuels.filter(f => avail.includes(f.id));
  const hasFull = cfg?.services?.full !== false;
  const rulesReady = !!params && !!cfg;

  // "Cambia": el producto se envía solo si está marcado. Se auto-marca al editar
  // (default = marcado si el valor difiere del precio actual); los no marcados
  // conservan su precio vigente y NO reciben push.
  const differs = (fid) => {
    const cur = row.prices?.[fid] || {};
    const v = values[fid] || {};
    return ['self', 'full'].some(svc => {
      const n = Number(v[svc]);
      if (v[svc] === '' || v[svc] == null || !Number.isFinite(n)) return false;
      const c = Number(cur[svc]);
      return !Number.isFinite(c) || Math.abs(n - c) > 0.0001;
    });
  };
  const isIncluded = (fid) => include[fid] ?? differs(fid);
  const toggleInclude = (fid) => setInclude(p => ({ ...p, [fid]: !isIncluded(fid) }));

  const edit = (fid, svc, val) => {
    setValues(prev => {
      const next = { ...prev, [fid]: { ...(prev[fid] || {}), [svc]: val } };
      if (svc !== 'self' || !rulesReady) return next;
      const base = Number(val);
      if (!Number.isFinite(base) || base <= 0) return next;
      const afd = (f) => Number(cfg?.product_adjustments?.[f]?.auto_full_diff ?? 1.1);
      const r2 = (v) => +v.toFixed(2);
      const put = (f, s, v) => {
        if (!avail.includes(f)) return;
        next[f] = { ...(next[f] || {}), [s]: r2(v) };
      };
      if (hasFull) put(fid, 'full', base + afd(fid));
      if (fid === 'regular') {
        const sup = base + Number(params.diff_super_regular || 0);
        put('super', 'self', sup);
        if (hasFull) put('super', 'full', sup + afd('super'));
        const vp = sup + Number(params.diff_vp_super || 0);
        put('vpower', 'self', vp);
        if (hasFull) put('vpower', 'full', vp + afd('vpower'));
      } else if (fid === 'diesel') {
        const ul = base + Number(params.diff_diesel_ulsd || 0);
        put('ulsd', 'self', ul);
        if (hasFull) put('ulsd', 'full', ul + afd('ulsd'));
      }
      return next;
    });
  };

  const submit = async () => {
    const approved = {};
    for (const f of shownFuels) {
      if (!isIncluded(f.id)) continue;
      const rowV = values[f.id] || {};
      const entry = {};
      for (const svc of ['self', 'full']) {
        const n = Number(rowV[svc]);
        if (rowV[svc] === '' || rowV[svc] == null || !Number.isFinite(n) || n <= 0) continue;
        if (n < 10 || n > 100) {
          toast.error(`${f.name}: precio fuera de rango (Q${n.toFixed(2)})`, { description: 'Los precios deben estar entre Q10 y Q100.' });
          return;
        }
        entry[svc] = +n.toFixed(2);
      }
      if (Object.keys(entry).length) approved[f.id] = entry;
    }
    if (!Object.keys(approved).length) {
      toast.error('Marca "Cambia" en al menos un producto con precio válido', { description: 'Solo los productos marcados se envían; los demás conservan su precio actual.' });
      return;
    }
    setSaving(true);
    try {
      const req = await api.post('/price-change-requests/manual', {
        station_id: row.id,
        our_prices: row.prices || {},
        notes: notes || 'Cambio manual desde Monitor (sin tracking)',
      });
      await api.patch(`/price-change-requests/${req.data.id}`, {
        action: 'approve',
        approved_prices: approved,
        notes: notes.trim(),
      });
      toast.success('Cambio de precios enviado', { description: 'Se creó el push · el capturador debe confirmar con foto del monolito (MO-P06).', duration: 8000 });
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error('No se pudo enviar el cambio', { description: e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Intenta de nuevo' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-xl bg-[#f8f6f1] max-h-[88vh] overflow-y-auto" data-testid="manual-change-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-[20px]">Cambiar precios · {row.name}</DialogTitle>
        </DialogHeader>
        <p className="text-[11.5px] text-[#166534] -mt-1">{rulesReady ? (<>Escribe <b>Regular</b> y <b>Diesel</b> en Auto → el resto se calcula por reglas. Todo editable. Solo los productos con <b>Cambia</b> marcado se envían; los demás conservan su precio actual.</>) : 'Cargando reglas de precios…'}</p>
        <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-wider text-[#8a8579] border-b border-[#e8e5de]">
                <th className="text-left py-2 px-3 font-normal">Producto</th>
                <th className="text-center py-2 px-1 font-normal">Cambia</th>
                <th className="text-right py-2 px-2 font-normal">Auto</th>
                {hasFull && <th className="text-right py-2 px-3 font-normal">Completo</th>}
              </tr>
            </thead>
            <tbody>
              {shownFuels.map(f => (
                <tr key={f.id} className={`border-b border-[#f3f0e9] last:border-0 ${isIncluded(f.id) ? 'bg-[#f0fdf4]' : ''}`}>
                  <td className="py-2 px-3 flex items-center gap-2">
                    <span className="w-1.5 h-4 rounded-full" style={{ background: f.color || '#8a8579' }}/>{f.name}
                  </td>
                  <td className="py-2 px-1 text-center">
                    <Checkbox
                      checked={isIncluded(f.id)}
                      onCheckedChange={() => toggleInclude(f.id)}
                      className="data-[state=checked]:bg-[#16a34a] data-[state=checked]:border-[#16a34a]"
                      data-testid={`manual-include-${f.id}`}
                    />
                  </td>
                  <td className="py-2 px-2 text-right">
                    <Input
                      type="number" step="0.01"
                      value={values[f.id]?.self ?? ''}
                      onChange={e => edit(f.id, 'self', e.target.value)}
                      disabled={!rulesReady}
                      className={`inline-block w-[92px] h-8 bg-white text-[12.5px] font-mono tabular-nums text-right disabled:opacity-50 ${(f.id === 'regular' || f.id === 'diesel') ? 'border-2 border-[#16a34a]' : ''}`}
                      data-testid={`manual-${f.id}-self`}
                    />
                  </td>
                  {hasFull && (
                    <td className="py-2 px-3 text-right">
                      <Input
                        type="number" step="0.01"
                        value={values[f.id]?.full ?? ''}
                        onChange={e => edit(f.id, 'full', e.target.value)}
                        disabled={!rulesReady}
                        className="inline-block w-[92px] h-8 bg-white text-[12.5px] font-mono tabular-nums text-right disabled:opacity-50"
                        data-testid={`manual-${f.id}-full`}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Notas (opcional) · llegan en la notificación del cambio al TM, capturadores y agentes"
          className="bg-white text-[12.5px] min-h-[64px]"
          data-testid="manual-notes"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9 rounded-full text-[12.5px]" data-testid="manual-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="h-9 px-5 rounded-full bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] text-[12.5px]" data-testid="manual-submit">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5"/> : <Send size={13} className="mr-1.5"/>}
            Enviar cambio
          </Button>
        </div>
        <p className="text-[10.5px] text-[#8a8579]">Al enviar: se crea el push a la estación (confirmación con foto en MO-P06), se notifica a capturadores, agentes y TM de la zona, y queda registrado como cambio manual aprobado en Tracking precios.</p>
      </DialogContent>
    </Dialog>
  );
};
